import { useEffect, useState, useRef } from "react";
import api from "../utils/api";

export default function StatusBar({ onRefresh, isAdmin }) {
  const [status, setStatus] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [interval, setIntervalSec] = useState(60);
  const [countdown, setCountdown] = useState(60);
  const [showIntervalModal, setShowIntervalModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const countdownRef = useRef(null);

  const load = () => api.get("/sensors/status").then(r => setStatus(r.data)).catch(() => {});

  const loadInterval = () => {
    if (isAdmin) {
      api.get("/admin/interval").then(r => {
        setIntervalSec(r.data.seconds);
        setCountdown(r.data.seconds);
      }).catch(() => {});
    }
  };

  const resetCountdown = (sec) => {
    setCountdown(sec);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { return sec; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    load();
    loadInterval();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin]);

  useEffect(() => {
    resetCountdown(interval);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [interval]);

  const triggerFetch = async () => {
    setFetching(true);
    try {
      await api.post("/sensors/fetch-now");
      resetCountdown(interval);
      setTimeout(() => { load(); onRefresh && onRefresh(); }, 4000);
    } finally {
      setTimeout(() => setFetching(false), 4000);
    }
  };

  if (!status) return null;

  const allOk = status.ok === status.total;
  const hasOffline = status.offline > 0;
  const pct = countdown / interval;
  const r = 13;
  const circ = 2 * Math.PI * r;
  const offset = circ * pct;
  const timerColor = pct > 0.4 ? "#3b82f6" : pct > 0.2 ? "#eab308" : "#ef4444";

  return (
    <>
      <div style={S.bar}>
        {/* Links: Sensor-Status */}
        <div style={S.indicators}>
          <Pill count={status.ok} label="Online" color="#22c55e" />
          {status.warning > 0 && <Pill count={status.warning} label="Verzögert" color="#eab308" />}
          {status.offline > 0 && <Pill count={status.offline} label="Offline" color="#ef4444" />}
          {status.unknown > 0 && <Pill count={status.unknown} label="Unbekannt" color="#64748b" />}
          <span style={{ ...S.overallDot, background: allOk ? "#22c55e" : hasOffline ? "#ef4444" : "#eab308" }} />
          <span style={S.overallLabel}>
            {allOk ? "Alle Sensoren aktiv" : hasOffline ? `${status.offline} Sensor(en) offline` : "Teilweise verzögert"}
          </span>
          {status.total_measurements != null && (
            <span style={S.measCount} title="Bisher gespeicherte Messpunkte (alle Sensoren)">
              {new Intl.NumberFormat("de-CH").format(status.total_measurements)} Messpunkte
            </span>
          )}
        </div>

        {/* Mitte: Countdown + Info */}
        <div style={S.center}>
          <div style={S.timerBox}>
            <svg width="34" height="34" viewBox="0 0 34 34">
              <circle cx="17" cy="17" r={r} fill="none" stroke="#1e3a5f" strokeWidth="2.5"/>
              <circle cx="17" cy="17" r={r} fill="none" stroke={timerColor} strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={circ - offset}
                transform="rotate(-90 17 17)"
                style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.5s" }}
              />
              <text x="17" y="21" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600" fontFamily="sans-serif">
                {countdown}
              </text>
            </svg>
            <div>
              <div style={S.timerLabel}>Nächster Abruf in {countdown}s</div>
              <div style={S.timerSub}>{status.total} Sensoren · {interval}s Intervall</div>
            </div>
          </div>

          <button onClick={() => setShowInfo(v => !v)} style={S.infoBtn} title="Messmethodik">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="7" cy="7" r="6" stroke="#64748b" strokeWidth="1.2"/>
              <rect x="6.3" y="6" width="1.4" height="4.5" rx="0.7" fill="#64748b"/>
              <circle cx="7" cy="4" r="0.9" fill="#64748b"/>
            </svg>
            Messmethodik
          </button>

          {isAdmin && (
            <button onClick={() => setShowIntervalModal(true)} style={S.intervalBtn}>
              ⚙ Intervall
            </button>
          )}
        </div>

        {/* Rechts: Letzter Abruf + Button */}
        <div style={S.right}>
          {status.last_fetch && (
            <span style={S.lastFetch}>
              Letzter Abruf: {new Date(status.last_fetch).toLocaleTimeString("de-CH")}
              {" "}({status.last_fetch_success}/{status.total} OK
              {status.last_fetch_duration_ms ? `, ${status.last_fetch_duration_ms}ms` : ""})
            </span>
          )}
          <button style={S.btn} onClick={triggerFetch} disabled={fetching}>
            {fetching ? "⏳ Abruf läuft…" : "↻ Jetzt abrufen"}
          </button>
          {status.version && <span style={S.version} title="Software-Version">v{status.version}</span>}
        </div>
      </div>

      {/* Info-Panel */}
      {showInfo && (
        <div style={S.infoPanel}>
          <button onClick={() => setShowInfo(false)} style={S.closeBtn}>✕</button>
          <div style={S.infoPanelTitle}>Messmethodik & Definitionen</div>
          <div style={S.infoGrid}>
            <div style={S.infoCard}>
              <div style={{ ...S.infoCardLabel, color: "#3b82f6" }}>WMO-Norm</div>
              <div style={S.infoCardText}>
                <b style={{ color: "#e2e8f0" }}>Windgeschwindigkeit</b>: 10-Minuten-Mittelwert in 10 m Höhe<br/>
                <b style={{ color: "#e2e8f0" }}>Böe</b>: Maximale Windgeschwindigkeit über 3 Sekunden
              </div>
            </div>
            <div style={S.infoCard}>
              <div style={{ ...S.infoCardLabel, color: "#f97316" }}>Ecowitt WS90 (dieses Gerät)</div>
              <div style={S.infoCardText}>
                <b style={{ color: "#e2e8f0" }}>Windgeschwindigkeit</b>: Momentanwert, Messung alle 2s<br/>
                <b style={{ color: "#e2e8f0" }}>Böe</b>: Maximum der letzten <b style={{ color: "#fbbf24" }}>28 Sekunden</b>
              </div>
            </div>
            <div style={{ ...S.infoCard, gridColumn: "1/-1" }}>
              <div style={{ ...S.infoCardLabel, color: "#22c55e" }}>Dieses System</div>
              <div style={S.infoCardText}>
                API-Abruf alle <b style={{ color: "#e2e8f0" }}>{interval} Sekunden</b> via Ecowitt Cloud API — das ist das <b style={{ color: "#e2e8f0" }}>Speicherraster</b> der Messreihe ·
                Sensor sendet alle 8.5s an Gateway · Daten gespeichert in PostgreSQL.<br/>
                Die <b style={{ color: "#e2e8f0" }}>28 Sekunden</b> sind dagegen das <b style={{ color: "#e2e8f0" }}>Böenfenster des Geräts</b>: jeder abgerufene Böenwert ist das Maximum der letzten 28s — zwei verschiedene Grössen, kein Widerspruch.{" "}
                {interval <= 28
                  ? <>Bei einem Abrufintervall von {interval}s ≤ 28s ist die Böenerfassung <b style={{ color: "#22c55e" }}>lückenlos</b>.</>
                  : <>Bei {interval}s Abruf deckt das Fenster ~28 von {interval}s ab — kurze Spitzen dazwischen können unerfasst bleiben, die gespeicherten Böen sind also eher <b style={{ color: "#fbbf24" }}>konservativ</b>.</>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Intervall-Modal (nur Admin) */}
      {showIntervalModal && isAdmin && (
        <IntervalModal
          current={interval}
          onSave={async (sec) => {
            await api.post("/admin/interval", { seconds: sec });
            setIntervalSec(sec);
            resetCountdown(sec);
            setShowIntervalModal(false);
          }}
          onClose={() => setShowIntervalModal(false)}
        />
      )}
    </>
  );
}

function IntervalModal({ current, onSave, onClose }) {
  const [value, setValue] = useState(current);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const presets = [26, 30, 60, 120, 300];

  const handleSave = async () => {
    if (!confirmed) return;
    setSaving(true);
    try { await onSave(value); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.modalOverlay}>
      <div style={S.modal}>
        <div style={S.modalTitle}>⚙ Abruf-Intervall ändern</div>
        <div style={S.modalWarning}>
          ⚠ Achtung: Eine Änderung des Intervalls beeinflusst die Messreihe und die Datendichte.
          Nur für technische Tests verwenden.
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={S.modalLabel}>Schnellwahl</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {presets.map(p => (
              <button key={p} onClick={() => setValue(p)}
                style={{ ...S.presetBtn, background: value === p ? "#1d4ed8" : "#1e3a5f" }}>
                {p}s {p === 26 ? "(min)" : p === 60 ? "(Standard)" : ""}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={S.modalLabel}>Manuell (26–3600 Sekunden)</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="range" min="26" max="300" step="1" value={value}
              onChange={e => setValue(parseInt(e.target.value))}
              style={{ flex: 1 }} />
            <span style={{ color: "#e2e8f0", fontWeight: 700, minWidth: 50, fontSize: 16 }}>{value}s</span>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
              style={{ width: 16, height: 16 }} />
            <span style={{ color: "#f97316", fontSize: 13 }}>
              Ich verstehe, dass diese Änderung die Messreihe beeinflusst.
            </span>
          </label>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={S.cancelBtn}>Abbrechen</button>
          <button onClick={handleSave} disabled={!confirmed || saving}
            style={{ ...S.saveBtn, opacity: confirmed ? 1 : 0.4 }}>
            {saving ? "Speichern…" : `Auf ${value}s setzen`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Pill({ count, label, color }) {
  return (
    <div style={{ ...S.pill, borderColor: color }}>
      <span style={{ ...S.pillDot, background: color }} />
      <span style={{ color, fontWeight: 700, fontSize: 12 }}>{count}</span>
      <span style={S.pillLabel}>{label}</span>
    </div>
  );
}

const S = {
  bar: { background: "#1e293b", borderBottom: "1px solid #334155", padding: "10px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  indicators: { display: "flex", alignItems: "center", gap: 8 },
  pill: { display: "flex", alignItems: "center", gap: 5, border: "1px solid", borderRadius: 20, padding: "3px 10px" },
  pillDot: { width: 7, height: 7, borderRadius: "50%" },
  pillLabel: { color: "#94a3b8", fontSize: 12 },
  overallDot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block" },
  overallLabel: { color: "#94a3b8", fontSize: 13 },
  center: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 },
  timerBox: { display: "flex", alignItems: "center", gap: 10, background: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 10, padding: "6px 14px" },
  timerLabel: { color: "#e2e8f0", fontSize: 12, fontWeight: 600 },
  timerSub: { color: "#475569", fontSize: 10, marginTop: 2 },
  infoBtn: { background: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 7, padding: "5px 11px", color: "#94a3b8", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  intervalBtn: { background: "#0f172a", border: "1px solid #334155", borderRadius: 7, padding: "5px 11px", color: "#64748b", fontSize: 12, cursor: "pointer" },
  right: { display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" },
  lastFetch: { color: "#94a3b8", fontSize: 12 },
  measCount: { color: "#94a3b8", fontSize: 12, background: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" },
  version: { color: "#475569", fontSize: 11, fontFamily: "monospace" },
  btn: { background: "#334155", color: "#e2e8f0", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  infoPanel: { background: "#0f172a", borderBottom: "1px solid #1e3a5f", padding: "16px 24px", position: "relative" },
  infoPanelTitle: { color: "#f1f5f9", fontWeight: 600, fontSize: 13, marginBottom: 12 },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  infoCard: { background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 8, padding: "10px 14px" },
  infoCardLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 },
  infoCardText: { color: "#94a3b8", fontSize: 12, lineHeight: 1.7 },
  closeBtn: { position: "absolute", top: 12, right: 16, background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 16 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#0f172a", border: "1px solid #334155", borderRadius: 14, padding: 28, width: 420, maxWidth: "90vw" },
  modalTitle: { color: "#f1f5f9", fontWeight: 700, fontSize: 16, marginBottom: 14 },
  modalWarning: { background: "#422006", border: "1px solid #92400e", borderRadius: 8, padding: "10px 14px", color: "#fbbf24", fontSize: 12, lineHeight: 1.6, marginBottom: 20 },
  modalLabel: { color: "#64748b", fontSize: 12, marginBottom: 8 },
  presetBtn: { color: "#e2e8f0", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  cancelBtn: { background: "#1e3a5f", color: "#94a3b8", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, cursor: "pointer" },
  saveBtn: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
};
